/**
 * Ханшийн тэгшитгэл (`/accounting/fx`, шийдвэр 2026-10-15).
 *
 * Өрийг ЦУГЛУУЛАГЧИЙН аяллын ханшаар бүртгэсэн (аппын «Солиулсан ханш» =
 * зарласан ₮ ÷ ₽). Төлөхдөө өөр ханшаар төлбөл зөрүү нь ханшийн олз/гарз:
 *   зөрүү = төлсөн ₮ − төлсөн ₽ × аяллын ханш   (эерэг = гарз)
 * Монголбанкны ханш оролцохгүй.
 *
 * «Төлсөн ₮» бүртгээгүй, эсвэл аялал нь ханшгүй (₽/₮ зарлаагүй) төлбөр
 * тооцоонд орохгүй — тоог нь анхааруулгаар харуулна.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDate, formatMnt, formatRate, formatRub, money } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { DataTable } from '../data-table';
import { Section } from '../section';
import { StatCard, StatGrid } from '../stat-card';
import { Empty, ErrorNote } from '../ui';
import { AccountingFilters, ExcelButton, saveTableXlsx, useAccountingFilter } from './shared';

interface Row {
  id: string;
  paidAt: string;
  supplier: { id: string; name: string };
  trip: string | null;
  amountRub: string;
  tripRate: string | null;
  paidMnt: string | null;
  paidRate: string | null;
  bookedMnt: string | null;
  diffMnt: string | null;
}
interface Meta {
  totals: { bookedMnt: string; paidMnt: string; diffMnt: string };
  byMonth: { month: string; diffMnt: string }[];
  noRate: number;
  noMnt: number;
}

/** Эерэг = гарз (улаан), сөрөг = олз */
const diff = (v: string | null) => {
  if (v === null) return '—';
  const d = money(v);
  if (d.isZero()) return '0₮';
  return d.gt(0)
    ? <span className="text-alert-ink">гарз {formatMnt(d)}</span>
    : <span className="text-accent-700">олз {formatMnt(d.abs())}</span>;
};

export function FxTab() {
  const f = useAccountingFilter();
  const q = useQuery({
    queryKey: ['accounting', 'fx', f.query()],
    queryFn: () => api<Envelope<Row[]> & { meta: Meta }>(`/accounting/fx?${f.query()}`),
  });
  const rows = q.data?.data ?? [];
  const meta = q.data?.meta;

  const excel = () =>
    saveTableXlsx({
      file: `hanshiin-tegshitgel-${f.from}-${f.to}.xlsx`,
      title: 'Ханшийн тэгшитгэл',
      period: `${f.from} — ${f.to} · ханш = цуглуулагчийн аяллын ханш · эерэг зөрүү = гарз`,
      headers: ['Огноо', 'Нийлүүлэгч', 'Аялал', 'Төлсөн ₽', 'Аяллын ханш', 'Бүртгэлийн ₮', 'Төлсөн ₮', 'Төлсөн ханш', 'Зөрүү ₮'],
      widths: [12, 22, 13, 14, 12, 16, 16, 12, 15],
      rows: rows.map((r) => [
        formatDate(r.paidAt), r.supplier.name, r.trip ?? '—', { money: r.amountRub }, r.tripRate ?? 'ханшгүй',
        r.bookedMnt ? { money: r.bookedMnt } : null, r.paidMnt ? { money: r.paidMnt } : '₮ бүртгээгүй',
        r.paidRate ?? null, r.diffMnt ? { money: r.diffMnt } : null,
      ]),
    });

  return (
    <div className="flex flex-col gap-3.5">
      <Section
        title="Ханшийн тэгшитгэл"
        subtitle="нийлүүлэгчийн төлбөр"
        note="Ханш = цуглуулагчийн аяллын «Солиулсан ханш». Монголбанкны ханш оролцохгүй."
        actions={<ExcelButton onClick={excel} disabled={rows.length === 0} />}
      >
        <AccountingFilters f={f} store={false} supplier />
        {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
        {meta && (meta.noMnt > 0 || meta.noRate > 0) ? (
          <ErrorNote>
            Тооцоонд ороогүй:
            {meta.noMnt > 0 ? <> {meta.noMnt} төлбөрт «Төлсөн ₮» бүртгээгүй (<Link to="/credit" className="underline">Зээл, төлөлт</Link>-д шинээр бүртгэнэ);</> : null}
            {meta.noRate > 0 ? <> {meta.noRate} төлбөрийн аялал ханшгүй (цуглуулагч ₽/₮ зарлаагүй).</> : null}
          </ErrorNote>
        ) : null}
        {meta ? (
          <StatGrid>
            <StatCard label="Аяллын ханшаар" value={formatMnt(meta.totals.bookedMnt)} />
            <StatCard label="Бодитоор төлсөн" value={formatMnt(meta.totals.paidMnt)} />
            <StatCard
              label={money(meta.totals.diffMnt).gt(0) ? 'Ханшийн гарз' : 'Ханшийн олз'}
              value={formatMnt(money(meta.totals.diffMnt).abs())}
            />
          </StatGrid>
        ) : null}
        <DataTable
          rows={rows}
          loading={q.isPending}
          rowKey={(r) => r.id}
          empty={<Empty>Энэ хугацаанд нийлүүлэгчид төлсөн төлбөр алга</Empty>}
          columns={[
            { key: 'at', header: 'Огноо', nowrap: true, cell: (r) => formatDate(r.paidAt) },
            { key: 'sup', header: 'Нийлүүлэгч', mobile: 'title', cell: (r) => r.supplier.name },
            { key: 'trip', header: 'Аялал', nowrap: true, cell: (r) => <span className="num">{r.trip ?? '—'}</span> },
            { key: 'rub', header: 'Төлсөн ₽', align: 'right', nowrap: true, cell: (r) => formatRub(r.amountRub) },
            { key: 'trate', header: 'Аяллын ханш', align: 'right', cell: (r) => (r.tripRate ? formatRate(r.tripRate) : 'ханшгүй') },
            { key: 'mnt', header: 'Төлсөн ₮', align: 'right', nowrap: true, cell: (r) => (r.paidMnt ? formatMnt(r.paidMnt) : '—') },
            { key: 'prate', header: 'Төлсөн ханш', align: 'right', cell: (r) => (r.paidRate ? formatRate(r.paidRate) : '—') },
            { key: 'diff', header: 'Зөрүү', align: 'right', nowrap: true, mobile: 'value', cell: (r) => diff(r.diffMnt) },
          ]}
        />
      </Section>

      {meta && meta.byMonth.length > 1 ? (
        <Section title="Сараар">
          <DataTable
            rows={meta.byMonth}
            rowKey={(m) => m.month}
            columns={[
              { key: 'm', header: 'Сар', mobile: 'title', cell: (m) => m.month },
              { key: 'd', header: 'Зөрүү', align: 'right', mobile: 'value', cell: (m) => diff(m.diffMnt) },
            ]}
          />
        </Section>
      ) : null}
    </div>
  );
}
